<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="sample-detail">
  <!-- Download Section -->
  <div class="download-section">
    <h3>Download Project Archive</h3>
    <p>Complete source code project with integration examples:</p>
    <a href="/samples/angular-integration/Athena.Auth.Angular.zip" class="download-btn" download>
      ⬇️ Athena.Auth.Angular.zip
    </a>
  </div>

  <!-- Overview Section -->
  <section class="section-overview">
    <h3>Project Overview</h3>
    <p>
      This project demonstrates how to implement <strong>Microsoft Authentication Library (MSAL)</strong> integration
      to authenticate and authorize users for accessing <strong>Athena APIs</strong>.
      Athena serves as a centralized API gateway that provides secure access to various backend services including IoT, GCP, and data mapping services.
    </p>

    <h4>Application Features:</h4>
    <ul>
      <li>✓ <strong>Authenticate users</strong> using Azure AD B2C through MSAL</li>
      <li>✓ <strong>Acquire access tokens</strong> for Athena API authorization</li>
      <li>✓ <strong>Make authenticated requests</strong> to various backend services through Athena</li>
      <li>✓ <strong>Handle token lifecycle</strong> including automatic refresh and error handling</li>
    </ul>
  </section>

  <!-- Project Structure Section -->
  <section class="section-structure">
    <h3>Project Structure</h3>
    <div class="code-block-light">
      <pre><code>├── angular.json              # Angular workspace configuration
├── package.json              # Project dependencies and scripts
├── tsconfig.json             # TypeScript configuration
├── src/
│   ├── index.html           # Main HTML template
│   ├── main.ts              # Application bootstrap
│   ├── styles.scss          # Global application styles
│   └── app/
│       ├── app.component.ts       # Main component logic
│       ├── app.config.ts          # Application configuration
│       ├── app.routes.ts          # Routing configuration
│       ├── auth-config.ts         # Authentication configuration
│       ├── admin/
│       │   └── login-page/       # Authentication UI
│       ├── models/
│       │   └── athena.models.ts   # TypeScript type definitions
│       └── services/
│           ├── athena.service.ts  # Athena API service
│           └── auth.service.ts    # MSAL authentication service</code></pre>
    </div>
  </section>

  <!-- Core Services Section -->
  <section class="section-services">
    <h3>Core Services</h3>

    <!-- Configuration Service -->
    <div class="service-card">
      <h4>1. Configuration Service (auth-config.ts)</h4>
      <p>The configuration service manages all authentication and API settings required for Athena integration:</p>

      <h5>Key Settings:</h5>
      <ul>
        <li><strong>Azure AD B2C Configuration:</strong> Client ID, Tenant ID for authentication</li>
        <li><strong>API Configuration:</strong> Base endpoint, subscription key, required scopes</li>
        <li><strong>MSAL Options:</strong> Token caching, cookie storage preferences</li>
        <li><strong>Redirect Configuration:</strong> Post-authentication redirect handling</li>
      </ul>

      <h5>Configuration Example:</h5>
      <div class="code-block">
        <pre><code>export const authConfig: Configuration = {{ '{' }}
  // Azure AD B2C Configuration
  auth: {{ '{' }}
    clientId: "your-client-id",
    authority: "https://b2cdaikindev.onmicrosoft.com/your-tenant-id",
    redirectUri: window.location.origin,
  {{ '}' }},

  // Athena API Configuration
  apiConfig: {{ '{' }}
    baseEndpoint: "https://ath-dataplane-cu-dev-apim.azure-api.net/api",
    subscriptionKey: "your-subscription-key",
    scopes: [
        'openid',
        'profile',
        'https://b2cdaikindev.onmicrosoft.com/.../athena.access'
    ]
  {{ '}' }},

  // MSAL Options
  cache: {{ '{' }}
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false
  {{ '}' }}
{{ '}' }};</code></pre>
      </div>
    </div>

    <!-- Authentication Service -->
    <div class="service-card">
      <h4>2. Authentication Service (auth.service.ts)</h4>
      <p>The authentication service provides a comprehensive <strong>MSAL wrapper</strong> that simplifies Microsoft authentication with Angular integration:</p>

      <h5>Key MSAL Features:</h5>
      <ul>
        <li><strong>Popup/Redirect Authentication:</strong> Supports both authentication methods</li>
        <li><strong>Silent Token Acquisition:</strong> Automatic token refresh with fallback to interactive</li>
        <li><strong>Account Management:</strong> Active account tracking and profile access</li>
        <li><strong>Reactive State Management:</strong> RxJS observables for authentication state</li>
        <li><strong>Event-Driven Architecture:</strong> Callbacks for login, logout, errors, and token events</li>
      </ul>

      <h5>Main Methods:</h5>
      <div class="code-block">
        <pre><code>// Authentication lifecycle
await authService.login();                    // Authenticate user
const isAuthenticated = authService.isAuthenticated(); // Check auth status
const profile = authService.getUserProfile(); // Get user information
await authService.logout();                   // Sign out user

// Token management
const token = await authService.getAccessToken(); // Get access token
const account = authService.getActiveAccount();   // Get current account

// Reactive observables
authService.isAuthenticated$.subscribe(isAuth => {{ '{' }} /* handle auth state */ {{ '}' }});
authService.userProfile$.subscribe(profile => {{ '{' }} /* handle user profile */ {{ '}' }});</code></pre>
      </div>
    </div>

    <!-- API Service -->
    <div class="service-card">
      <h4>3. Athena API Service (athena.service.ts)</h4>
      <p>The Athena API service handles all HTTP requests with automatic authentication and error handling using Angular's HttpClient:</p>

      <h5>Key Features:</h5>
      <ul>
        <li><strong>Automatic Authentication:</strong> Ensures user is authenticated before API calls</li>
        <li><strong>Token Management:</strong> Automatically includes Bearer tokens and subscription keys</li>
        <li><strong>Request/Response Interceptors:</strong> Extensible middleware for request/response processing</li>
        <li><strong>HTTP Method Support:</strong> GET, POST, PUT, DELETE, PATCH operations</li>
        <li><strong>Error Handling:</strong> Comprehensive error handling with detailed information</li>
        <li><strong>Loading State Management:</strong> RxJS observables for loading indicators</li>
        <li><strong>Event Callbacks:</strong> Request lifecycle event handling</li>
      </ul>
    </div>
  </section>

  <!-- Authorization Section -->
  <section class="section-authorization">
    <h3>How to Authorize Requests</h3>

    <!-- Authentication Flow -->
    <div class="subsection">
      <h4>Authentication Flow</h4>

      <h5>1. Services Initialization</h5>
      <div class="code-block">
        <pre><code>import {{ '{' }} AuthService {{ '}' }} from './services/auth.service';
import {{ '{' }} AthenaService {{ '}' }} from './services/athena.service';

constructor(
  private authService: AuthService,
  private athenaService: AthenaService
) {{ '{' }} {{ '}' }}</code></pre>
      </div>

      <h5>2. User Authentication</h5>
      <div class="code-block">
        <pre><code>// Authenticate user (popup method)
await this.authService.login();

// Check authentication
if (this.authService.isAuthenticated()) {{ '{' }}
    console.log("User successfully authenticated");
{{ '}' }}

// Subscribe to authentication state
this.authService.isAuthenticated$.subscribe(isAuth => {{ '{' }}
    console.log("Authentication status:", isAuth);
{{ '}' }});</code></pre>
      </div>

      <h5>3. Executing Authenticated API Requests through Athena</h5>
      <p>
        The <code>AthenaService</code> automatically handles:
      </p>
      <ul>
        <li>User authentication verification</li>
        <li>Access token acquisition and refresh</li>
        <li>Authorization headers (Bearer token)</li>
        <li>API subscription key headers</li>
        <li>Base URL construction</li>
      </ul>

      <h5>API Request Examples:</h5>
      <div class="code-block">
        <pre><code>// Get IoT devices
try {{ '{' }}
    const devices = await this.athenaService.get('devices');
    console.log('Devices retrieved:', devices);
{{ '}' }} catch (error) {{ '{' }}
    console.error('API request failed:', error);
{{ '}' }}

// Get device points with parameters
const devicePoints = await this.athenaService.get('device-points', {{ '{' }}
    deviceId: 'SOLU2001P00002',
    sourceId: '3101057'
{{ '}' }});

// Get product details (POST request)
const productDetails = await this.athenaService.post('product/details', {{ '{' }}
    data: {{ '{' }}
        productName: "wme-c"
    {{ '}' }}
{{ '}' }});

// Get data points from visualization service
const dataPoints = await this.athenaService.get('data-points');</code></pre>
      </div>
    </div>
  </section>

  <!-- Getting Started Section -->
  <section class="section-getting-started">
    <h3>Getting Started</h3>

    <h4>Prerequisites</h4>
    <ul>
      <li>Node.js (v18 or higher recommended)</li>
      <li>Angular CLI 19.x</li>
      <li>Modern web browser with ES6+ support</li>
      <li>Valid Athena API credentials and subscription key</li>
    </ul>

    <h4>Installation and Setup</h4>
    <ol>
      <li>
        <strong>Install Dependencies</strong>
        <div class="code-block">
          <pre><code>npm install</code></pre>
        </div>
      </li>
      <li>
        <strong>Configure Authentication</strong>
        <p>Update <code>src/app/auth-config.ts</code> with your Athena credentials:</p>
        <ul>
          <li>Client ID and Tenant ID from Azure AD B2C</li>
          <li>Athena API base endpoint and subscription key</li>
          <li>Required API scopes for your application</li>
        </ul>
      </li>
      <li>
        <strong>Run Development Server</strong>
        <div class="code-block">
          <pre><code>ng serve</code></pre>
        </div>
        <p>The application will be available at <code>http://localhost:4200</code></p>
      </li>
    </ol>

    <h4>Development Scripts</h4>
    <div class="code-block">
      <pre><code>ng serve              # Start development server
ng build              # Build for production
ng build --watch      # Build in watch mode
ng test               # Run unit tests</code></pre>
    </div>
  </section>

  <!-- API Examples Section -->
  <section class="section-api-examples">
    <h3>API Request Examples</h3>

    <h4>Complete Request Flow</h4>
    <div class="code-block">
      <pre><code>// 1. Inject services (handled by Angular DI)
constructor(
  private authService: AuthService,
  private athenaService: AthenaService
) {{ '{' }} {{ '}' }}

// 2. Set up reactive subscriptions
ngOnInit() {{ '{' }}
  // Subscribe to authentication state
  this.authService.isAuthenticated$.subscribe(isAuth => {{ '{' }}
    console.log("Authentication status:", isAuth);
  {{ '}' }});

  // Subscribe to loading state
  this.athenaService.loading$.subscribe(isLoading => {{ '{' }}
    console.log("API loading:", isLoading);
  {{ '}' }});
{{ '}' }}

// 3. Authenticate user
async authenticateUser() {{ '{' }}
  try {{ '{' }}
    await this.authService.login();
    console.log("User authenticated successfully");
  {{ '}' }} catch (error) {{ '{' }}
    console.error("Authentication failed:", error);
  {{ '}' }}
{{ '}' }}

// 4. Make API requests
async loadData() {{ '{' }}
  try {{ '{' }}
    const devices = await this.athenaService.get('devices');
    const products = await this.athenaService.post('product/details', {{ '{' }}
      data: {{ '{' }} productName: 'wme-c' {{ '}' }}
    {{ '}' }});
    const dataPoints = await this.athenaService.get('data-points');
    console.log("All data loaded successfully");
  {{ '}' }} catch (error) {{ '{' }}
    console.error("Failed to load data:", error);
  {{ '}' }}
{{ '}' }}</code></pre>
    </div>

    <h4>Request Headers</h4>
    <p>All Athena requests automatically include:</p>
    <div class="code-block">
      <pre><code>Authorization: Bearer {{ '{' }}access_token{{ '}' }}
Content-Type: application/json
X-Subscription-Key: {{ '{' }}subscription_key{{ '}' }}</code></pre>
    </div>
  </section>

  <!-- Benefits Section -->
  <section class="section-benefits">
    <h3>Architecture Benefits</h3>
    <ul>
      <li><strong>Separation of Concerns:</strong> Configuration separated from logic</li>
      <li><strong>Reusability:</strong> Angular authentication can be integrated into multiple services</li>
      <li><strong>Maintainability:</strong> Easier to update and maintain authentication logic</li>
      <li><strong>Flexibility:</strong> Easy to configure without changing code</li>
      <li><strong>Type Safety:</strong> Full TypeScript support with type definitions</li>
      <li><strong>Error Handling:</strong> Centralized error handling and reactive state management</li>
      <li><strong>Modular Architecture:</strong> Clean separation between auth, service, and configuration layers</li>
    </ul>
  </section>

  <!-- Support Section -->
  <section class="section-support">
    <h3>Support</h3>
    <p>
      If you encounter issues with Athena API integration or authentication,
      please refer to the <a href="/api-mesh/samples#getting-started">API Mesh documentation</a> or contact the API Mesh support team (<a href="mailto:Athena_Support&#64;daacloud.onmicrosoft.com">Athena_Support&#64;daacloud.onmicrosoft.com</a>).
    </p>
  </section>
</div>
